import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ServerPagination from '../components/ServerPagination';
import Filters from '../components/Filters';
import AccordionList from '../components/AccordionList';
import ExportButtons from '../components/ExportButtons';
import PageSizeSelector from '../components/PageSizeSelector';
import { getApiLogsWithFilters, setFilters, resetFilters } from '../redux/apiLogSlice';
import { fetchApiLogsWithFilters } from '../services/api';
import { handleExportResponse } from '../utils/exportUtils';
import RecordsLoader from '../components/RecordsLoader';
import { StatusBadge } from '../components/Badges';

const ApiLogs = () => {
    const dispatch = useDispatch();
    const { logs, status, pagination, error } = useSelector((state) => state.apiLogs);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [showFilters, setShowFilters] = useState(false);

    const [localFilters, setLocalFilters] = useState({
        endpoint: '',
        method: '',
        log_level: '',
        status_code: '',
        error_type: '',
        user_id: '',
        user_email: '',
        user_type: '',
        organization_id: '',
        view_class: '',
        view_function: '',
        created_at_from: '',
        created_at_to: '',
    });

    // Filter configuration
    const filterConfig = [
        {
            key: 'endpoint',
            label: 'Endpoint',
            type: 'text',
            placeholder: 'Enter endpoint path',
        },
        {
            key: 'method',
            label: 'HTTP Method',
            type: 'select',
            options: [
                { value: 'GET', label: 'GET' },
                { value: 'POST', label: 'POST' },
                { value: 'PUT', label: 'PUT' },
                { value: 'DELETE', label: 'DELETE' },
                { value: 'PATCH', label: 'PATCH' },
            ],
        },
        {
            key: 'log_level',
            label: 'Log Level',
            type: 'select',
            options: [
                { value: 'ERROR', label: 'Error' },
                { value: 'WARNING', label: 'Warning' },
                { value: 'INFO', label: 'Info' },
            ],
        },
        {
            key: 'status_code',
            label: 'Status Code',
            type: 'number',
            placeholder: 'Enter status code (e.g., 200, 404, 500)',
            min: 100,
            max: 599,
        },
        {
            key: 'error_type',
            label: 'Error Type',
            type: 'text',
            placeholder: 'Enter error type (e.g., ValueError)',
        },
        {
            key: 'user_id',
            label: 'User ID',
            type: 'number',
            placeholder: 'Enter user ID',
            min: 0,
        },
        {
            key: 'user_email',
            label: 'User Email',
            type: 'text',
            placeholder: 'Enter user email',
        },
        {
            key: 'user_type',
            label: 'User Type',
            type: 'select',
            options: [
                { value: 'user', label: 'User' },
                { value: 'organization', label: 'Organization' },
            ],
        },
        {
            key: 'organization_id',
            label: 'Organization ID',
            type: 'number',
            placeholder: 'Enter organization ID',
            min: 0,
        },
        {
            key: 'view_class',
            label: 'View Class',
            type: 'text',
            placeholder: 'Enter view class name',
        },
        {
            key: 'view_function',
            label: 'View Function',
            type: 'text',
            placeholder: 'Enter view function name',
        },
        {
            key: 'created_at_from',
            label: 'Created Date From',
            type: 'date',
        },
        {
            key: 'created_at_to',
            label: 'Created Date To',
            type: 'date',
        },
    ];

    // Fetch logs with current filters and pagination
    const fetchLogs = useCallback(() => {
        const params = {
            page: currentPage,
            page_size: pageSize,
            ...localFilters,
        };
        
        // Remove empty filter values
        Object.keys(params).forEach(key => {
            if (params[key] === '' || params[key] === null || params[key] === undefined) {
                delete params[key];
            }
        });

        dispatch(getApiLogsWithFilters(params));
    }, [dispatch, currentPage, pageSize, localFilters]);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    const handleFilterChange = (filters) => {
        setLocalFilters(filters);
        dispatch(setFilters(filters));
        setCurrentPage(1);
    };

    const handleFilterReset = (filters) => {
        setLocalFilters(filters);
        dispatch(resetFilters());
        setCurrentPage(1);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (e) => {
        setPageSize(Number(e.target.value));
        setCurrentPage(1);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '—';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
        });
    };

    const formatExecutionTime = (ms) => {
        if (!ms && ms !== 0) return '—';
        if (ms < 1000) return `${ms}ms`;
        return `${(ms / 1000).toFixed(2)}s`;
    };

    const getStatusColor = (statusCode) => {
        if (!statusCode) return '';
        if (statusCode >= 200 && statusCode < 300) return 'text-green-600';
        if (statusCode >= 300 && statusCode < 400) return 'text-blue-600';
        if (statusCode >= 400 && statusCode < 500) return 'text-yellow-600';
        if (statusCode >= 500) return 'text-red-600';
        return '';
    };

    const handleExport = async (format) => {
        try {
            const exportParams = { ...localFilters, export: format };
            
            Object.keys(exportParams).forEach(key => {
                if (exportParams[key] === '' || exportParams[key] === null || exportParams[key] === undefined) {
                    delete exportParams[key];
                }
            });

            const response = await fetchApiLogsWithFilters(exportParams);
            handleExportResponse(response, format, `api-logs.${format}`);
        } catch (error) {
            console.error('Export error:', error);
            alert(error.message || 'Failed to export API logs. Please try again.');
        }
    };

    return (
        <div className="container mx-auto p-6">
            {status === 'loading' && <RecordsLoader text="Loading API Logs, please hold on..." />}

            <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl text-black font-bold">API Logs</h1>
                <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors font-medium flex items-center gap-2"
                >
                    <svg
                        className={`w-5 h-5 transition-transform duration-200 ${showFilters ? 'transform rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                        />
                    </svg>
                    {showFilters ? 'Hide Filters' : 'Show Filters'}
                </button>
            </div>

            {/* Filters */}
            {showFilters && (
                <div className="mb-4">
                    <Filters
                        filters={filterConfig}
                        onFilterChange={handleFilterChange}
                        onReset={handleFilterReset}
                    />
                </div>
            )}

            {/* Export Buttons */}
            <div className="mb-4">
                <ExportButtons
                    onExport={handleExport}
                    isLoading={status === 'loading'}
                />
            </div>

            {/* Page Size Selector */}
            <div className="mb-4">
                <PageSizeSelector
                    pageSize={pageSize}
                    onPageSizeChange={handlePageSizeChange}
                />
            </div>

            {status === 'failed' && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    Error loading API Logs: {error || 'Unknown error'}
                </div>
            )}

            {/* Logs Accordion List */}
            {status === 'succeeded' && (
                <>
                    <AccordionList
                        data={logs || []}
                        getRowKey={(log) => log.id}
                        primaryFields={[
                            {
                                key: 'index',
                                label: '#',
                                render: (value, item, index) => {
                                    // Calculate descending index based on current page and page size
                                    const page = pagination?.page || currentPage;
                                    const pageSizeValue = pagination?.page_size || pageSize;
                                    const totalCount = pagination?.total_count || 0;
                                    const startIndex = (page - 1) * pageSizeValue;
                                    return totalCount - (startIndex + index);
                                },
                            },
                            {
                                key: 'endpoint',
                                label: 'Endpoint',
                                flex: true,
                            },
                            {
                                key: 'method',
                                label: 'Method',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'log_level',
                                label: 'Log Level',
                                render: (value) => <StatusBadge status={value} type={value === 'ERROR' ? 'error' : value === 'WARNING' ? 'warning' : 'generic'} />,
                            },
                            {
                                key: 'status_code',
                                label: 'Status',
                                render: (value) => value ? (
                                    <span className={`font-semibold ${getStatusColor(value)}`}>
                                        {value}
                                    </span>
                                ) : '—',
                            },
                            {
                                key: 'created_at',
                                label: 'Created At',
                                render: (value) => formatDate(value),
                            },
                        ]}
                        expandedFields={[
                            {
                                key: 'view_class',
                                label: 'View Class',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'view_function',
                                label: 'View Function',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'request_path',
                                label: 'Request Path',
                                render: (value) => value || '—',
                                mono: true,
                            },
                            {
                                key: 'request_ip',
                                label: 'Request IP',
                                render: (value) => value || '—',
                                mono: true,
                            },
                            {
                                key: 'user_agent',
                                label: 'User Agent',
                                render: (value) => value ? (
                                    <div className="text-xs break-words max-w-md">{value}</div>
                                ) : '—',
                            },
                            {
                                key: 'user_id',
                                label: 'User ID',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'user_email',
                                label: 'User Email',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'user_type',
                                label: 'User Type',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'organization_id',
                                label: 'Organization ID',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'error_type',
                                label: 'Error Type',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'error_message',
                                label: 'Error Message',
                                render: (value) => {
                                    if (!value) return '—';
                                    return (
                                        <div className="bg-red-50 border border-red-200 rounded p-2 text-sm text-red-800">
                                            {value}
                                        </div>
                                    );
                                },
                            },
                            {
                                key: 'error_traceback',
                                label: 'Error Traceback',
                                render: (value) => {
                                    if (!value) return '—';
                                    return (
                                        <div className="bg-red-50 border border-red-200 rounded p-2 text-xs font-mono text-red-800 max-h-32 overflow-y-auto">
                                            {value}
                                        </div>
                                    );
                                },
                            },
                            {
                                key: 'error_details',
                                label: 'Error Details',
                                render: (value) => {
                                    if (!value) return '—';
                                    return (
                                        <pre className="bg-gray-50 border border-gray-200 rounded p-2 text-xs font-mono max-h-32 overflow-y-auto">
                                            {JSON.stringify(value, null, 2)}
                                        </pre>
                                    );
                                },
                            },
                            {
                                key: 'request_headers',
                                label: 'Request Headers',
                                render: (value) => {
                                    if (!value) return '—';
                                    return (
                                        <pre className="bg-gray-50 border border-gray-200 rounded p-2 text-xs font-mono max-h-32 overflow-y-auto">
                                            {JSON.stringify(value, null, 2)}
                                        </pre>
                                    );
                                },
                            },
                            {
                                key: 'request_body',
                                label: 'Request Body',
                                render: (value) => {
                                    if (!value) return '—';
                                    return (
                                        <pre className="bg-gray-50 border border-gray-200 rounded p-2 text-xs font-mono max-h-32 overflow-y-auto">
                                            {value}
                                        </pre>
                                    );
                                },
                            },
                            {
                                key: 'query_params',
                                label: 'Query Parameters',
                                render: (value) => {
                                    if (!value) return '—';
                                    return (
                                        <pre className="bg-gray-50 border border-gray-200 rounded p-2 text-xs font-mono max-h-32 overflow-y-auto">
                                            {JSON.stringify(value, null, 2)}
                                        </pre>
                                    );
                                },
                            },
                            {
                                key: 'response_body',
                                label: 'Response Body',
                                render: (value) => {
                                    if (!value) return '—';
                                    return (
                                        <pre className="bg-gray-50 border border-gray-200 rounded p-2 text-xs font-mono max-h-32 overflow-y-auto">
                                            {value}
                                        </pre>
                                    );
                                },
                            },
                            {
                                key: 'execution_time_ms',
                                label: 'Execution Time',
                                render: (value) => formatExecutionTime(value),
                            },
                            {
                                key: 'request_timestamp',
                                label: 'Request Timestamp',
                                render: (value) => formatDate(value),
                            },
                            {
                                key: 'module_name',
                                label: 'Module Name',
                                render: (value) => value || '—',
                                mono: true,
                            },
                            {
                                key: 'function_name',
                                label: 'Function Name',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'line_number',
                                label: 'Line Number',
                                render: (value) => value || '—',
                            },
                            {
                                key: 'metadata',
                                label: 'Metadata',
                                render: (value) => {
                                    if (!value) return '—';
                                    return (
                                        <pre className="bg-gray-50 border border-gray-200 rounded p-2 text-xs font-mono max-h-32 overflow-y-auto">
                                            {JSON.stringify(value, null, 2)}
                                        </pre>
                                    );
                                },
                            },
                        ]}
                        emptyMessage="No API logs found!"
                        allowMultipleExpand={true}
                    />

                    {/* Server-side Pagination */}
                    {pagination && pagination.total_pages > 0 && (
                        <ServerPagination
                            currentPage={pagination.page || currentPage}
                            totalPages={pagination.total_pages || 1}
                            onPageChange={handlePageChange}
                            totalCount={pagination.total_count || 0}
                            pageSize={pagination.page_size || pageSize}
                            showPageInfo={true}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default ApiLogs;
